// Calendar days as "YYYY-MM-DD" keys in the user's local time zone. Arithmetic
// runs on UTC day numbers, so daylight saving time never shifts a day.
// Weeks start on Monday; weekdays are 0 (Monday) … 6 (Sunday).

export type DayKey = string;
export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

const DAY_MS = 86_400_000;
const KEY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

const pad = (n: number, len = 2) => String(n).padStart(len, '0');

/** Local calendar day of a Date. */
export function toDayKey(date: Date): DayKey {
  return `${pad(date.getFullYear(), 4)}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function todayKey(now: Date = new Date()): DayKey {
  return toDayKey(now);
}

export function isDayKey(value: unknown): value is DayKey {
  if (typeof value !== 'string') return false;
  const m = KEY_RE.exec(value);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const date = new Date(Date.UTC(y, mo - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === mo - 1 && date.getUTCDate() === d;
}

/** Days since 1970-01-01. */
export function dayNumber(key: DayKey): number {
  const m = KEY_RE.exec(key);
  if (!m) throw new Error(`invalid day key: ${key}`);
  return Math.round(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / DAY_MS);
}

export function fromDayNumber(n: number): DayKey {
  const date = new Date(n * DAY_MS);
  return `${pad(date.getUTCFullYear(), 4)}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}

export function addDays(key: DayKey, days: number): DayKey {
  return fromDayNumber(dayNumber(key) + days);
}

/** b − a in days. */
export function diffDays(a: DayKey, b: DayKey): number {
  return dayNumber(b) - dayNumber(a);
}

export function weekday(key: DayKey): Weekday {
  // 1970-01-01 was a Thursday (3).
  return ((((dayNumber(key) + 3) % 7) + 7) % 7) as Weekday;
}

/** Monday of the week containing `key`. */
export function startOfWeek(key: DayKey): DayKey {
  return addDays(key, -weekday(key));
}

export function startOfMonth(key: DayKey): DayKey {
  return `${key.slice(0, 8)}01`;
}

/** First day of the month `delta` months after the month of `key`. */
export function addMonths(key: DayKey, delta: number): DayKey {
  const y = Number(key.slice(0, 4));
  const m = Number(key.slice(5, 7)) - 1 + delta;
  const year = y + Math.floor(m / 12);
  const month = ((m % 12) + 12) % 12;
  return `${pad(year, 4)}-${pad(month + 1)}-01`;
}

/** Mon–Sun rows covering the month of `key`, including the neighbouring days that fill the first and last row. */
export function monthGrid(key: DayKey): DayKey[][] {
  const first = startOfMonth(key);
  const next = addMonths(first, 1);
  const rows: DayKey[][] = [];
  for (let day = startOfWeek(first); day < next; day = addDays(day, 7)) {
    rows.push(Array.from({ length: 7 }, (_, i) => addDays(day, i)));
  }
  return rows;
}

export function sameMonth(a: DayKey, b: DayKey): boolean {
  return a.slice(0, 7) === b.slice(0, 7);
}

/** A Date at local noon of the day (for Intl formatting only). */
export function dayToDate(key: DayKey): Date {
  return new Date(Number(key.slice(0, 4)), Number(key.slice(5, 7)) - 1, Number(key.slice(8, 10)), 12);
}
